<script setup lang="ts">
import PageContainer from "~/components/ui/PageContainer.vue";
import ExerciseMCQ from "~/features/computer/components/ExerciseMCQ.vue";
import { useExercises } from "~/features/computer/composables/useExercises";
import { chapterHubByUnit } from "~/features/computer/data/chapter-hubs";
import { buildComputerCollectionSchema } from "~/features/computer/data/seo/computer-schema";
import { computerMcqChapterByUnit } from "~/features/computer/data/mcq-registry";
import type { ComputerMcqUnit } from "~/features/computer/types/computer";

const props = defineProps<{
  unit: Exclude<ComputerMcqUnit, "python">;
}>();

const exercises = useExercises(props.unit);
const quickQuestions = exercises.mcqs.slice(0, 3);
const config = chapterHubByUnit[props.unit];
const chapter = computerMcqChapterByUnit[props.unit];
const longQuestionsCard = config.practiceCards.find((item) => item.title === "Long Questions");
const faqs = [
  {
    question: `Chapter ${config.chapterNumber} ${chapter.shortTitle} exercise kaise use karein?`,
    answer: `Pehle quick MCQs attempt karein, phir short questions aur long questions par move karein. Yeh page quick start ke liye bana hai.`,
  },
  {
    question: `Chapter ${config.chapterNumber} ${chapter.shortTitle} ke practice questions kahan milte hain?`,
    answer: `Is exercise page par quick MCQs milte hain, aur baqi full practice links isi page ke neeche diye gaye hain.`,
  },
  {
    question: `Kya exercise page exam prep ke liye enough hai?`,
    answer: `Exercise page quick revision ke liye useful hai, lekin complete prep ke liye MCQs, short questions aur long questions pages bhi kholne chahiye.`,
  },
];
const relatedItems = [
  { name: `Chapter ${config.chapterNumber} ${chapter.shortTitle}`, path: chapter.routes.chapterTo },
  { name: `Chapter ${config.chapterNumber} ${chapter.shortTitle} MCQs`, path: chapter.routes.mcqTo },
  { name: `Chapter ${config.chapterNumber} ${chapter.shortTitle} Short Questions`, path: config.practiceCards[1].to },
  ...(longQuestionsCard ? [{ name: `Chapter ${config.chapterNumber} ${chapter.shortTitle} Long Questions`, path: longQuestionsCard.to }] : []),
  { name: "Important Questions", path: "/11th/computer/important-questions/" },
];

usePageSeo({
  title: `Chapter ${config.chapterNumber} ${chapter.shortTitle} Exercise | Ustaad`,
  description: `Chapter ${config.chapterNumber} ${chapter.shortTitle} quick MCQs aur full exercise links Roman Urdu me.`,
  keywords: `chapter ${config.chapterNumber} ${chapter.shortTitle.toLowerCase()} exercise, 11th class computer exercise`,
  faqs,
  breadcrumbs: [
    { name: "Home", item: "/" },
    { name: "11th Computer", item: "/11th/computer/" },
    { name: `Ch ${config.chapterNumber} ${chapter.shortTitle}`, item: chapter.routes.chapterTo },
    { name: "Exercise", item: chapter.routes.exerciseTo },
  ],
  schemaType: "CollectionPage",
  schemaProperties: buildComputerCollectionSchema({
    about: `Chapter ${config.chapterNumber} ${chapter.shortTitle} Exercise`,
    items: relatedItems,
  }),
});
</script>

<template>
  <PageContainer>
    <div class="space-y-8">
      <section class="rounded-3xl bg-slate-950 p-6 text-white">
        <h1 class="text-2xl font-bold tracking-tight">Chapter {{ config.chapterNumber }} {{ chapter.shortTitle }} Quick Exercise</h1>
        <p class="mt-3 text-sm leading-7 text-slate-200">Important MCQs pehle yahin attempt karo, phir short aur long questions par move karo.</p>
      </section>

      <section class="space-y-4 rounded-3xl border border-slate-200 bg-white p-5">
        <ExerciseMCQ v-for="question in quickQuestions" :key="question.id" :question="question" />
      </section>

      <section class="rounded-3xl border border-slate-200 bg-slate-50 p-5 dark:border-slate-800 dark:bg-slate-950">
        <h2 class="text-lg font-semibold text-slate-900 dark:text-white">Related Pages</h2>
        <div class="mt-4 grid gap-3">
          <NuxtLink :to="chapter.routes.mcqTo" class="rounded-2xl border border-slate-200 bg-white p-4 hover:border-sky-300 dark:border-slate-800 dark:bg-slate-900">More MCQs</NuxtLink>
          <NuxtLink :to="config.practiceCards[1].to" class="rounded-2xl border border-slate-200 bg-white p-4 hover:border-sky-300 dark:border-slate-800 dark:bg-slate-900">Short Questions</NuxtLink>
          <NuxtLink v-if="config.practiceCards[2]" :to="config.practiceCards[2].to" class="rounded-2xl border border-slate-200 bg-white p-4 hover:border-sky-300 dark:border-slate-800 dark:bg-slate-900">Long Questions</NuxtLink>
          <NuxtLink to="/11th/computer/important-questions/" class="rounded-2xl border border-slate-200 bg-white p-4 hover:border-sky-300 dark:border-slate-800 dark:bg-slate-900">Important Questions</NuxtLink>
          <NuxtLink :to="chapter.routes.chapterTo" class="rounded-2xl border border-slate-200 bg-white p-4 hover:border-sky-300 dark:border-slate-800 dark:bg-slate-900">Back to Chapter</NuxtLink>
        </div>
      </section>

      <section class="rounded-3xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
        <h2 class="text-lg font-semibold text-slate-900 dark:text-white">FAQs</h2>
        <div class="mt-4 space-y-3">
          <article
            v-for="faq in faqs"
            :key="faq.question"
            class="rounded-2xl bg-slate-50 p-4 ring-1 ring-slate-200 dark:bg-slate-950 dark:ring-slate-800"
          >
            <h3 class="text-sm font-semibold text-slate-900 dark:text-slate-100">{{ faq.question }}</h3>
            <p class="mt-2 text-sm leading-7 text-slate-700 dark:text-slate-300">{{ faq.answer }}</p>
          </article>
        </div>
      </section>
    </div>
  </PageContainer>
</template>
